/**
 * Product review fallback (product cards + product-page inline rating).
 *
 * Yotpo's `bottomLine` widget renders no markup for products that have 0 reviews,
 * so those would show nothing. This reveals a self-contained placeholder (empty
 * SVG stars + "0 Reviews") ONLY for widgets that stay empty after Yotpo has had
 * time to load. Widgets that Yotpo populates (products with reviews) keep the
 * live Yotpo rating and never show the placeholder.
 *
 * The empty stars are drawn with our OWN inline SVG (snippets/review-stars-empty),
 * NOT Yotpo's icon font — on pages where Yotpo's font isn't loaded (e.g. the cart
 * page, whose review widgets are injected via AJAX) the icon-font glyph rendered
 * as missing-glyph boxes. To still match the live rating's size, we measure a real
 * rendered Yotpo star and expose it as `--gk-review-star-size` (persisted in
 * localStorage so every page reuses the same size). The decision of which widgets
 * are empty is based on what Yotpo actually rendered — NOT the reviews metafield,
 * which is out of sync with Yotpo on this store.
 *
 * Flicker guard: once a widget has shown content it is LATCHED populated and its
 * placeholder is never revealed again, so transient empties during Yotpo's own
 * re-render cycles can't make the rating flash on/off.
 */
(function () {
  if (window.__gkReviewFallbackInit) return;
  window.__gkReviewFallbackInit = true;

  // 0 件フォールバックを適用するコンテナ。カード（.product-card--reviews-link）と
  // 商品ページのタイトル横インライン評価（.product--reviews-inline）の両方を対象にする。
  var CONTAINERS = ['.product-card--reviews-link', '.product--reviews-inline'];
  var CONTAINER_SEL = CONTAINERS.join(', ');
  var WIDGET_SEL = CONTAINERS.map(function (c) {
    return c + ' .yotpo.bottomLine';
  }).join(', ');
  var STORE_KEY = 'gkReviewStarMetrics';
  var templateCaptured = false;

  // 実測した星のサイズを保存／復元する。これにより、レビュー有り商品が並ぶページで
  // 測った値を、Yotpo が読み込まれない／空のページの 0 件フォールバックでもそのまま
  // 使え、どのページでも星の大きさが一致する。
  function applyStoredMetrics() {
    try {
      var raw = localStorage.getItem(STORE_KEY);
      if (!raw) return;
      var m = JSON.parse(raw);
      if (m.size) document.documentElement.style.setProperty('--gk-review-star-size', m.size);
    } catch (e) {}
  }
  applyStoredMetrics();

  // True once Yotpo has injected a rating (stars and/or review-count text).
  function widgetHasContent(widget) {
    if (!widget) return false;
    if (
      widget.querySelector(
        '.yotpo-stars, .yotpo-icon-star, .yotpo-icon-empty-star, .yotpo-icon-half-star, .sr-only, .text-m'
      )
    ) {
      return true;
    }
    return widget.textContent.trim().length > 0;
  }

  // 一度でも中身が入ったウィジェットは「populated」としてラッチする。Yotpo が再描画の
  // 途中で一瞬空になっても、ラッチ済みなら二度とプレースホルダーを再表示しない
  // （これがレビュー表示のチカチカ＝点滅を防ぐ）。
  function markPopulated(widget) {
    if (widget && widgetHasContent(widget)) widget.setAttribute('data-gk-populated', '1');
  }
  function everPopulated(widget) {
    return !!(widget && widget.getAttribute('data-gk-populated') === '1');
  }

  // Measure a real rendered Yotpo star so the SVG placeholder matches its size,
  // and capture the review wording ("Reviews") from a populated widget.
  function captureTemplate() {
    if (templateCaptured) return;
    var widgets = document.querySelectorAll(WIDGET_SEL);
    var tpl = null;
    for (var i = 0; i < widgets.length; i++) {
      if (widgetHasContent(widgets[i])) {
        tpl = widgets[i];
        break;
      }
    }
    if (!tpl) return;

    var starsEl = tpl.querySelector('.yotpo-stars');
    var star = tpl.querySelector('.yotpo-icon');
    // 実際にレンダリングされた星の高さ（≒視覚的な星の大きさ）を採用する。取れない時は
    // アイコンの font-size にフォールバック。
    var sizePx = null;
    if (starsEl) {
      var h = starsEl.getBoundingClientRect().height;
      if (h) sizePx = Math.round(h) + 'px';
    }
    if (!sizePx && star) sizePx = getComputedStyle(star).fontSize;
    if (sizePx) {
      document.documentElement.style.setProperty('--gk-review-star-size', sizePx);
      try {
        localStorage.setItem(STORE_KEY, JSON.stringify({ size: sizePx }));
      } catch (e) {}
    }

    var count = tpl.querySelector('.text-m');
    if (count) {
      // e.g. "68 Reviews" -> wording "Reviews"
      var m = count.textContent.trim().match(/^[\d.,\s]+(.+)$/);
      window.__gkReviewWord = m && m[1] ? m[1].trim() : null;
    }

    templateCaptured = true;
    syncCount();
  }

  function syncCount() {
    if (!window.__gkReviewWord) return;
    var nodes = document.querySelectorAll('.product-card--reviews-empty .text-m');
    for (var i = 0; i < nodes.length; i++) {
      nodes[i].textContent = '0 ' + window.__gkReviewWord;
    }
  }

  function eachCard(cb) {
    var links = document.querySelectorAll(CONTAINER_SEL);
    for (var i = 0; i < links.length; i++) {
      var placeholder = links[i].querySelector('.product-card--reviews-empty');
      if (!placeholder) continue;
      cb(placeholder, links[i].querySelector('.yotpo.bottomLine'));
    }
  }

  // Safe anytime: only HIDES the placeholder once its widget is (ever) populated.
  function hidePopulated() {
    captureTemplate();
    eachCard(function (placeholder, widget) {
      markPopulated(widget);
      if (everPopulated(widget)) placeholder.hidden = true;
    });
  }

  // After a grace period: reveal placeholders whose widget was NEVER populated.
  function revealEmpties() {
    captureTemplate();
    eachCard(function (placeholder, widget) {
      markPopulated(widget);
      placeholder.hidden = everPopulated(widget);
    });
    syncCount();
  }

  var observer = new MutationObserver(hidePopulated);
  function observeWidgets() {
    var widgets = document.querySelectorAll(WIDGET_SEL);
    for (var i = 0; i < widgets.length; i++) {
      observer.observe(widgets[i], { childList: true, subtree: true });
    }
  }

  // Ask Yotpo to (re)scan the DOM and initialize any widgets it hasn't yet —
  // needed for cards injected via AJAX after Yotpo's initial load. Covers both the
  // legacy (`yotpo.refreshWidgets`) and newer (`yotpoWidgetsContainer.initWidgets`)
  // APIs. Yotpo may not be ready when this first runs, so retry for a while.
  function refreshYotpo(attempt) {
    attempt = attempt || 0;
    var done = false;
    if (window.yotpo && typeof window.yotpo.refreshWidgets === 'function') {
      try {
        window.yotpo.refreshWidgets();
        done = true;
      } catch (e) {}
    }
    if (window.yotpoWidgetsContainer && typeof window.yotpoWidgetsContainer.initWidgets === 'function') {
      try {
        window.yotpoWidgetsContainer.initWidgets();
        done = true;
      } catch (e) {}
    }
    if (done) return;
    if (attempt < 20) {
      setTimeout(function () {
        refreshYotpo(attempt + 1);
      }, 500);
    }
  }

  function scanPasses() {
    observeWidgets();
    // Give Yotpo time to load before revealing empties, so reviewed products
    // don't briefly flash the fallback. Multiple passes cover slow loads.
    setTimeout(revealEmpties, 2000);
    setTimeout(revealEmpties, 4500);
    setTimeout(revealEmpties, 8000);
  }

  function init() {
    scanPasses();
  }

  // Public: call after new product cards are injected into the DOM (e.g. AJAX
  // product recommendations / recently viewed / infinite scroll). Re-initializes
  // Yotpo on the new widgets so real ratings appear, then re-runs the fallback.
  window.gkReviewFallbackRescan = function () {
    refreshYotpo();
    scanPasses();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
